import { BodyCell, DataGrid } from '@tableau/data-grid-1-wrapper';
import * as React from 'react';

import { AngularContext } from '../../../../ts/factories/AngularContextFactory';
import { IActionButton } from '../../../../commonInfra/contentActions/ActionPanelInterfaces';
import { CommonStyles } from '../../CommonStyles';
import { IReactTranslateService } from '../../IReactTranslateService';
import { DeprecatedReactActionsMenu, IActionTarget } from '../ActionsMenu';
import { AngularDirective } from '../AngularDirective';

/**
 * The ActionsCell wraps an existing cell formatter with another formatter that adds an actions menu
 */

export interface ActionsCellOptions<TSummary, TDetail> {
  actions: IActionButton<TSummary, TDetail>[];
  translate: IReactTranslateService;
  getActionTarget: () => IActionTarget<TSummary, TDetail>;
  testId?: string;
  menuDisabled?: (row: DataGrid.Row) => boolean;
}

export interface ActionsCellState {
  allowedActionsHackyUpdate: number;
}

export function ActionsCell<TSummary, TDetail>(
    ContentFormatter: React.ComponentType<BodyCell.Props>,
    options: ActionsCellOptions<TSummary, TDetail>,
    onActionCellClicked: (row: DataGrid.Row) => void,
    cellSize = CommonStyles.Sizes.CellHeight,
    $scope?: any): React.ComponentFactory<BodyCell.Props, any> {

  return React.createFactory(class extends React.Component<BodyCell.Props, ActionsCellState> {
    displayName: 'ActionsCell';

    state = { allowedActionsHackyUpdate: 0 };

    private cellStyle = { position: 'relative' as 'relative' };

    private menuStyle = {
      position: 'absolute' as 'absolute',
      right: 0,
      top: 0
    };

    private contentStyle = {
      // Reserve enough space for the action menu button and padding
      marginRight: CommonStyles.Sizes.CellHeight + CommonStyles.Sizes.DefaultSpace,
      overflow: 'hidden' as 'hidden',
      textOverflow: 'ellipsis'
    };

    private handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
      onActionCellClicked(this.props.row);
      event.stopPropagation();
    }

    public generateUpdateCallback($scope: any){
      const self = this;
      const scope = $scope;
      return () => {
        scope.onAllowedActionsUpdate = () =>
          {
            self.setState({ allowedActionsHackyUpdate: self.state.allowedActionsHackyUpdate + 1 });
          };
      };
    }

    render() {

      const { getActionTarget, actions, translate } = options;

      const menu = DeprecatedReactActionsMenu.element({
        getActionTarget,
        actions,
        translate,
        buttonStyle: {
          width: cellSize,
          height: cellSize,
        },
        buttonHoverStyle: {
          backgroundColor: CommonStyles.Colors.White,
          opacity: 0.9,
        },
        attachUpdateTriggerToScope: this.generateUpdateCallback($scope)
      });

      const content: JSX.Element = <ContentFormatter {...this.props} />;

      if (options.menuDisabled && options.menuDisabled(this.props.row)) {
        return content;
      }

      /* eslint-disable jsx-a11y/no-static-element-interactions */
      /* eslint-disable jsx-a11y/click-events-have-key-events */
      return (
        <div style={ this.cellStyle } data-tb-test-id={ options.testId } >
          <div style={ this.contentStyle }>
            { content }
          </div>
          <div style={ this.menuStyle } onClick={ this.handleClick } data-tb-test-id={ 'actions-menu' } >
            { menu }
          </div>
        </div>
      );
    }
  });
}

// TODO - Story 616235: Port React actions menu to use Stacked Elements, and remove Angular actions cell
// still used in RevisionHistoryDialogCtrl
export function AngularActionsCell(angularContext: AngularContext): React.StatelessComponent<any> {
  return function AngularActionsCell() {
    return AngularDirective.element(
      {
        angularContext: angularContext,
        template:
        '<tb:popup-menu>' +
        "  <tb:popup-menu-custom-button class='tb-data-grid-action-menu'>" +
        "    <span class='tb-action-icon' data-tb-test-id='action-menu-button'></span>" +
        '  </tb:popup-menu-custom-button>' +
        '  <tb:popup-menu-content>' +
        "    <div ng-include=\"'actionMenu.html'\"></div>" +
        '  </tb:popup-menu-content>' +
        '</tb:popup-menu>'
      }
    );
  };
}
